import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiCheckAll  } from '@mdi/js'
import { apiClient } from '../../api/client'
import { refreshAssetProperties, refreshIssues } from '../../hooks/useQueries'
import { SECTION_ICONS } from '../../lib/section-icons'

interface PendingValue {
  uid: string
  property_uid?: string | null
  property_definition?: {
    uid?: string
    code?: string
    name?: Record<string, string>
    type?: string
  } | null
  value?: any
  source_details?: { page?: number; confidence?: number; snippet?: string } | null
}

interface PendingAssetGroup {
  asset_uid: string
  asset_name?: string | null
  asset_type_code?: string | null
  pending_count: number
  values: PendingValue[]
}

interface PendingResponse {
  document_uid: string
  total_pending: number
  high_confidence_count: number
  assets: PendingAssetGroup[]
}

interface Props {
  org: string
  documentUid: string
}

export function DocumentPendingApprovals({ org, documentUid }: Props) {
  const { t, i18n } = useTranslation('documents')
  const queryClient = useQueryClient()

  const queryKey = useMemo(() => ['document-pending-approvals', org, documentUid], [org, documentUid])

  const { data, isLoading } = useQuery<PendingResponse>({
    queryKey,
    queryFn: async () => {
      const { data: resp, error } = await apiClient.GET(
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/pending-approvals',
        { params: { path: { orgCode: org, documentUid } } } as any,
      )
      if (error) throw new Error(typeof error === 'string' ? error : JSON.stringify(error))
      return resp as PendingResponse
    },
    staleTime: 1000 * 60 * 5,
  })

  const [batchProcessing, setBatchProcessing] = useState(false)
  const [processingRow, setProcessingRow] = useState<Set<string>>(new Set())
  const [processingAsset, setProcessingAsset] = useState<Set<string>>(new Set())
  const [partial, setPartial] = useState<string | null>(null)

  const refresh = useCallback(
    (assets: PendingAssetGroup[]) => {
      queryClient.invalidateQueries({ queryKey })
      for (const a of assets) refreshAssetProperties(queryClient, org, a.asset_uid)
      refreshIssues(queryClient, org)
    },
    [queryClient, queryKey, org],
  )

  const approveOne = useCallback(
    async (assetUid: string, valueUid: string, all: PendingAssetGroup[]) => {
      setProcessingRow(prev => new Set(prev).add(valueUid))
      try {
        await apiClient.POST(
          '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve',
          {
            params: { path: { orgCode: org, assetUid } },
            body: { approve: [valueUid], reject: [] },
          } as any,
        )
        refresh(all)
      } finally {
        setProcessingRow(prev => {
          const s = new Set(prev)
          s.delete(valueUid)
          return s
        })
      }
    },
    [org, refresh],
  )

  const rejectOne = useCallback(
    async (assetUid: string, valueUid: string, all: PendingAssetGroup[]) => {
      setProcessingRow(prev => new Set(prev).add(valueUid))
      try {
        await apiClient.POST(
          '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve',
          {
            params: { path: { orgCode: org, assetUid } },
            body: { approve: [], reject: [valueUid] },
          } as any,
        )
        refresh(all)
      } finally {
        setProcessingRow(prev => {
          const s = new Set(prev)
          s.delete(valueUid)
          return s
        })
      }
    },
    [org, refresh],
  )

  const validateAsset = useCallback(
    async (assetUid: string, valueUids: string[], all: PendingAssetGroup[]) => {
      if (valueUids.length === 0) return
      setProcessingAsset(prev => new Set(prev).add(assetUid))
      try {
        await apiClient.POST(
          '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve',
          {
            params: { path: { orgCode: org, assetUid } },
            body: { approve: valueUids, reject: [] },
          } as any,
        )
        refresh(all)
      } finally {
        setProcessingAsset(prev => {
          const s = new Set(prev)
          s.delete(assetUid)
          return s
        })
      }
    },
    [org, refresh],
  )

  const validateAll = useCallback(async () => {
    if (!data || data.assets.length === 0) return
    setBatchProcessing(true)
    setPartial(null)
    try {
      const calls = data.assets.map(a =>
        apiClient.POST(
          '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve',
          {
            params: { path: { orgCode: org, assetUid: a.asset_uid } },
            body: { approve: a.values.map(v => v.uid), reject: [] },
          } as any,
        ),
      )
      const results = await Promise.allSettled(calls)
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) {
        setPartial(t('pending_approvals.batch_partial_failure', { ok: results.length - failed, total: results.length, failed }))
      }
      refresh(data.assets)
    } finally {
      setBatchProcessing(false)
    }
  }, [data, org, refresh, t])

  const validateHighConfidence = useCallback(async () => {
    if (!data || data.assets.length === 0) return
    setBatchProcessing(true)
    setPartial(null)
    try {
      const calls = data.assets.map(a =>
        apiClient.POST(
          '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve',
          {
            params: { path: { orgCode: org, assetUid: a.asset_uid } },
            body: { approve: [], reject: [], min_confidence: 0.85 },
          } as any,
        ),
      )
      await Promise.allSettled(calls)
      refresh(data.assets)
    } finally {
      setBatchProcessing(false)
    }
  }, [data, org, refresh])

  if (isLoading || !data) return null
  if (data.total_pending === 0) return null

  const locale = i18n.language || 'en'
  const propName = (v: PendingValue) => {
    const n = v.property_definition?.name
    if (!n) return v.property_definition?.code || v.property_uid || ''
    return n[locale] || n.en || v.property_definition?.code || ''
  }

  return (
    <div className="space-y-3 mb-6 border border-border rounded-lg p-4 bg-amber-50/30 dark:bg-amber-900/10" data-testid="document-pending-approvals">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            {t('pending_approvals.title')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t('pending_approvals.summary', {
              count: data.total_pending,
              assetCount: data.assets.length,
            })}
            {data.high_confidence_count > 0 && ' '}
            {data.high_confidence_count > 0 && t('pending_approvals.high_confidence_suffix', { count: data.high_confidence_count })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.total_pending > 1 && (
            <Button
              variant="default"
              size="sm"
              onClick={validateAll}
              disabled={batchProcessing}
              data-testid="document-validate-all-pending-btn"
            >
              <AppIcon path={mdiCheckAll} size="sm" className="mr-1" />
              {t('pending_approvals.validate_all', { count: data.total_pending })}
            </Button>
          )}
          {data.high_confidence_count > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={validateHighConfidence}
              disabled={batchProcessing}
              data-testid="document-validate-high-confidence-btn"
            >
              <AppIcon path={mdiCheckAll} size="sm" className="mr-1" />
              {t('pending_approvals.validate_high_confidence', { count: data.high_confidence_count })}
            </Button>
          )}
        </div>
      </div>

      {partial && (
        <p className="text-xs text-destructive" data-testid="document-validate-all-error">{partial}</p>
      )}

      <div className="space-y-2">
        {data.assets.map(a => (
          <details
            key={a.asset_uid}
            className="border border-border rounded-md bg-card"
            open={a.pending_count <= 10}
            data-testid={`document-pending-asset-${a.asset_uid}`}
          >
            <summary className="cursor-pointer px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-muted/40">
              <Link
                to="/$org/assets/$assetUid"
                params={{ org, assetUid: a.asset_uid }}
                className="font-medium text-foreground hover:underline"
                onClick={e => e.stopPropagation()}
              >
                {a.asset_name || a.asset_uid}
              </Link>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-muted-foreground">
                  {t('pending_approvals.asset_group_count', { count: a.pending_count })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    validateAsset(a.asset_uid, a.values.map(v => v.uid), data.assets)
                  }}
                  disabled={processingAsset.has(a.asset_uid) || batchProcessing}
                  title={t('pending_approvals.validate_asset', { count: a.pending_count })}
                  data-testid={`document-validate-asset-${a.asset_uid}`}
                >
                  <AppIcon path={mdiCheckAll} size="xs" />
                </Button>
              </div>
            </summary>
            <div className="px-3 py-2 border-t border-border/50">
              {a.values.map(v => (
                <div key={v.uid} className="flex items-center gap-2 py-1.5 border-b last:border-b-0 border-border/40">
                  <span className="text-sm text-foreground flex-shrink min-w-0 truncate" title={propName(v)}>
                    {propName(v)}
                  </span>
                  <span className="text-sm font-mono text-foreground flex-shrink min-w-0 truncate">
                    {typeof v.value === 'string' ? v.value : JSON.stringify(v.value)}
                  </span>
                  <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
                    {v.source_details?.page != null && (
                      <span
                        className="text-xs text-muted-foreground inline-flex items-center gap-1 flex-shrink-0"
                        title={v.source_details?.snippet || undefined}
                      >
                        <AppIcon path={SECTION_ICONS.documents} size="xs" className="flex-shrink-0" />
                        p.{v.source_details.page}
                      </span>
                    )}
                    {v.source_details?.confidence != null && (
                      <span className="text-[11px] text-muted-foreground flex-shrink-0">
                        {Math.round(v.source_details.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => approveOne(a.asset_uid, v.uid, data.assets)}
                      disabled={processingRow.has(v.uid)}
                      data-testid={`document-approve-value-${v.uid}`}
                    >
                      <AppIcon path={mdiCheck} size="xs" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => rejectOne(a.asset_uid, v.uid, data.assets)}
                      disabled={processingRow.has(v.uid)}
                      data-testid={`document-reject-value-${v.uid}`}
                    >
                      <AppIcon path={mdiClose} size="xs" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  )
}
